import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Palette, ShieldCheck, FileText, Clock, CurrencyInr, Sparkle, UploadSimple,
  FloppyDisk, Trash, CheckCircle, Eye, PencilSimple, ArrowsClockwise, DownloadSimple, Table,
  GitBranch, Coins, Plus, EnvelopeSimple, CalendarBlank, Receipt,
} from "@phosphor-icons/react";
import SetupWizardPanel from "@/pages/admin/SetupWizardPanel";
import ApprovalChainsPanel from "@/pages/admin/ApprovalChainsPanel";
import EmailSenderPanel from "@/pages/admin/EmailSenderPanel";
import EmailLogPanel from "@/pages/admin/EmailLogPanel";
import LeaveRulesPanel from "@/pages/admin/LeaveRulesPanel";
import PayslipSettingsPanel from "@/pages/admin/PayslipSettingsPanel";

const POLICY_KINDS = [
  { v: "leave", label: "Leave Policy" },
  { v: "attendance", label: "Attendance Policy" },
  { v: "wfh", label: "Work from Home" },
  { v: "notice_period", label: "Notice Period" },
  { v: "code_of_conduct", label: "Code of Conduct" },
  { v: "posh", label: "POSH — Sexual Harassment" },
  { v: "it_usage", label: "IT & Data Usage" },
  { v: "travel", label: "Travel & Expense" },
  { v: "reimbursement", label: "Reimbursement" },
  { v: "referral", label: "Referral Programme" },
];

const LETTER_KINDS = [
  { v: "offer_letter", label: "Offer Letter" },
  { v: "appointment_letter", label: "Appointment Letter" },
  { v: "confirmation", label: "Confirmation" },
  { v: "experience_letter", label: "Experience Certificate" },
  { v: "relieving_letter", label: "Relieving Letter" },
  { v: "salary_revision", label: "Salary Revision" },
  { v: "promotion", label: "Promotion" },
  { v: "warning", label: "Warning" },
  { v: "pip", label: "PIP" },
  { v: "no_dues", label: "No Dues" },
  { v: "transfer", label: "Transfer" },
  { v: "internship", label: "Internship" },
];

export default function AdminPanel() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-bold">Admin</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Company settings</h1>
        <p className="text-sm text-zinc-500 mt-2">Everything you need to run HR for your tenant — branding, policies, templates, timesheet rules and payroll defaults, in one place.</p>
      </div>

      <Tabs defaultValue="wizard">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="wizard" className="rounded-sm" data-testid="admin-tab-wizard"><Sparkle size={14} className="mr-1" /> Setup wizard</TabsTrigger>
          <TabsTrigger value="branding" className="rounded-sm" data-testid="admin-tab-branding"><Palette size={14} className="mr-1" /> Branding</TabsTrigger>
          <TabsTrigger value="email" className="rounded-sm" data-testid="admin-tab-email"><EnvelopeSimple size={14} className="mr-1" /> Email sender</TabsTrigger>
          <TabsTrigger value="policies" className="rounded-sm" data-testid="admin-tab-policies"><ShieldCheck size={14} className="mr-1" /> Policies</TabsTrigger>
          <TabsTrigger value="leave-rules" className="rounded-sm" data-testid="admin-tab-leave-rules"><CalendarBlank size={14} className="mr-1" /> Leave definitions</TabsTrigger>
          <TabsTrigger value="payslip" className="rounded-sm" data-testid="admin-tab-payslip"><Receipt size={14} className="mr-1" /> Payslip format</TabsTrigger>
          <TabsTrigger value="letters" className="rounded-sm" data-testid="admin-tab-letters"><FileText size={14} className="mr-1" /> Letter templates</TabsTrigger>
          <TabsTrigger value="timesheet" className="rounded-sm" data-testid="admin-tab-timesheet"><Clock size={14} className="mr-1" /> Timesheet ↔ Attendance</TabsTrigger>
          <TabsTrigger value="salary" className="rounded-sm" data-testid="admin-tab-salary"><CurrencyInr size={14} className="mr-1" /> Salary defaults</TabsTrigger>
          <TabsTrigger value="salary_templates" className="rounded-sm" data-testid="admin-tab-salary-templates"><Coins size={14} className="mr-1" /> Salary templates</TabsTrigger>
          <TabsTrigger value="bulk" className="rounded-sm" data-testid="admin-tab-bulk"><Table size={14} className="mr-1" /> Bulk import</TabsTrigger>
          <TabsTrigger value="onbkit" className="rounded-sm" data-testid="admin-tab-onbkit"><UploadSimple size={14} className="mr-1" /> Onboarding kit</TabsTrigger>
          <TabsTrigger value="chains" className="rounded-sm" data-testid="admin-tab-chains"><GitBranch size={14} className="mr-1" /> Approval chains</TabsTrigger>
        </TabsList>

        <TabsContent value="wizard" className="mt-4"><SetupWizardPanel params={params} /></TabsContent>
        <TabsContent value="branding" className="mt-4"><BrandingPanel params={params} /></TabsContent>
        <TabsContent value="email" className="mt-4 space-y-4"><EmailSenderPanel params={params} /><EmailLogPanel params={params} /></TabsContent>
        <TabsContent value="policies" className="mt-4"><PoliciesPanel params={params} /></TabsContent>
        <TabsContent value="leave-rules" className="mt-4"><LeaveRulesPanel params={params} /></TabsContent>
        <TabsContent value="payslip" className="mt-4"><PayslipSettingsPanel params={params} /></TabsContent>
        <TabsContent value="letters" className="mt-4"><LettersPanel params={params} /></TabsContent>
        <TabsContent value="timesheet" className="mt-4"><TimesheetPanel params={params} /></TabsContent>
        <TabsContent value="salary" className="mt-4"><SalaryPanel params={params} /></TabsContent>
        <TabsContent value="salary_templates" className="mt-4"><SalaryTemplatesPanel params={params} /></TabsContent>
        <TabsContent value="bulk" className="mt-4"><BulkImportPanel params={params} /></TabsContent>
        <TabsContent value="onbkit" className="mt-4"><OnboardingKitPanel params={params} /></TabsContent>
        <TabsContent value="chains" className="mt-4"><ApprovalChainsPanel params={params} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ------ Branding ------
function ImageSlot({ label, path, onUpload, onClear, hint, testid, busy }) {
  const ref = useRef(null);
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/60" data-testid={`${testid}-slot`}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        <div className="w-28 h-16 border-2 border-dashed border-zinc-200 rounded-sm grid place-items-center bg-white overflow-hidden">
          {path ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`} alt={label} className="max-w-full max-h-full object-contain" data-testid={`${testid}-img`} /> : <UploadSimple size={18} className="text-zinc-400" />}
        </div>
        <div className="space-y-1">
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} data-testid={`${testid}-file`} />
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} className="rounded-sm h-7" data-testid={`${testid}-btn`}><UploadSimple size={12} className="mr-1" /> {path ? "Replace" : "Upload"}</Button>
            {path && <Button variant="ghost" size="sm" onClick={onClear} className="rounded-sm h-7 text-red-600" data-testid={`${testid}-clear`}>Remove</Button>}
          </div>
          <div className="text-[10px] text-zinc-500">{hint}</div>
        </div>
      </div>
    </div>
  );
}

function SignatureStamp({ b, setB, params }) {
  const [busy, setBusy] = useState(false);
  const up = async (file, key, purpose) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/uploads?purpose=${purpose}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB({ ...b, [key]: data.path }); toast.success(`${purpose === "signature" ? "Signature" : "Stamp"} uploaded — click Save branding`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const preview = async () => {
    try { const { data } = await api.post("/admin/branding/letter-preview", null, { params, responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3 pt-2 border-t border-zinc-100" data-testid="signature-stamp-section">
      <div className="flex items-center justify-between">
        <div><div className="text-sm font-semibold">Authorised signature &amp; company stamp</div><div className="text-xs text-zinc-500">Printed at the end of every generated letter (offer, appointment, increment, relieving, bulk letters) so they are print-ready.</div></div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={b.show_signature_on_letters !== false} onChange={(e) => setB({ ...b, show_signature_on_letters: e.target.checked })} data-testid="admin-sig-toggle" /> Show on letters</label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ImageSlot label="Signature image" path={b.signature_path} busy={busy} testid="admin-signature" hint="PNG with transparent background · ~600×200 px" onUpload={(f) => up(f, "signature_path", "signature")} onClear={() => setB({ ...b, signature_path: "" })} />
        <ImageSlot label="Company stamp / seal" path={b.stamp_path} busy={busy} testid="admin-stamp" hint="Round or square PNG · ~500×500 px" onUpload={(f) => up(f, "stamp_path", "stamp")} onClear={() => setB({ ...b, stamp_path: "" })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Signatory name</Label><Input value={b.signatory_name || ""} onChange={(e) => setB({ ...b, signatory_name: e.target.value })} placeholder="e.g. Ananya Sharma" className="rounded-sm mt-1" data-testid="admin-signatory-name" /></div>
        <div><Label className="text-xs">Signatory title</Label><Input value={b.signatory_title || ""} onChange={(e) => setB({ ...b, signatory_title: e.target.value })} placeholder="e.g. Head – Human Resources" className="rounded-sm mt-1" data-testid="admin-signatory-title" /></div>
      </div>
      <Button variant="outline" size="sm" onClick={preview} className="rounded-sm" data-testid="admin-branding-letter-preview">Preview sample letter (PDF) — uses saved branding</Button>
    </div>
  );
}

function BrandingPanel({ params }) {
  const [b, setB] = useState({ logo_path: "", primary_color: "#1a2d59", accent_color: "#33cc33", secondary_color: "#33cc33", letter_header_text: "HUMAN RESOURCES", tagline: "", website: "", letter_footer: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/admin/branding", { params }).then(({ data }) => setB(data)); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads?purpose=logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB({ ...b, logo_path: data.path });
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const save = async () => {
    try {
      await api.post("/admin/branding", { ...b, ...params });
      toast.success("Branding saved · Zenith360 HR watermark retained on all letters");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 border border-zinc-200 bg-white rounded-sm p-6 space-y-4">
        <div>
          <Label className="text-xs">Company logo</Label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-28 h-28 border-2 border-dashed border-zinc-200 rounded-sm grid place-items-center bg-zinc-50 overflow-hidden">
              {b.logo_path ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${b.logo_path}`} alt="logo" className="max-w-full max-h-full object-contain" />
              ) : <UploadSimple size={22} className="text-zinc-400" />}
            </div>
            <div className="space-y-1.5">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="admin-logo-file" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-sm" data-testid="admin-logo-btn"><UploadSimple size={14} className="mr-1" /> {busy ? "Uploading…" : "Upload logo"}</Button>
              <div className="text-xs text-zinc-500">PNG or SVG · square works best · max 2MB.</div>
              <div className="text-[10px] text-zinc-400">Zenith360 HR branding is retained on all letters and login as a watermark.</div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Primary colour (letter header band)</Label><div className="flex gap-1 mt-1"><input type="color" value={b.primary_color || "#1a2d59"} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="w-10 h-9 rounded-sm border border-zinc-300 cursor-pointer" data-testid="admin-color-primary-picker" /><Input value={b.primary_color} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="rounded-sm font-mono-plex flex-1" data-testid="admin-color-primary" /></div></div>
          <div><Label className="text-xs">Secondary colour (thin strip)</Label><div className="flex gap-1 mt-1"><input type="color" value={b.secondary_color || b.accent_color || "#33cc33"} onChange={(e) => setB({ ...b, secondary_color: e.target.value })} className="w-10 h-9 rounded-sm border border-zinc-300 cursor-pointer" data-testid="admin-color-secondary-picker" /><Input value={b.secondary_color || b.accent_color || ""} onChange={(e) => setB({ ...b, secondary_color: e.target.value })} className="rounded-sm font-mono-plex flex-1" data-testid="admin-color-secondary" /></div></div>
        </div>
        <div><Label className="text-xs">Letter header tag (top-right in blue band)</Label><Input value={b.letter_header_text || ""} onChange={(e) => setB({ ...b, letter_header_text: e.target.value })} placeholder="HUMAN RESOURCES" className="rounded-sm mt-1 uppercase tracking-widest text-sm" data-testid="admin-header-text" /></div>
        <div><Label className="text-xs">Tagline</Label><Input value={b.tagline || ""} onChange={(e) => setB({ ...b, tagline: e.target.value })} placeholder="e.g. Building the future of work" className="rounded-sm" /></div>
        <div><Label className="text-xs">Website</Label><Input value={b.website || ""} onChange={(e) => setB({ ...b, website: e.target.value })} placeholder="https://…" className="rounded-sm font-mono-plex" /></div>
        <div><Label className="text-xs">Letter footer text</Label><Textarea rows={2} value={b.letter_footer || ""} onChange={(e) => setB({ ...b, letter_footer: e.target.value })} placeholder="Registered address, GSTIN, PAN…" className="rounded-sm" /></div>
        <SignatureStamp b={b} setB={setB} params={params} />
        <div className="flex justify-end"><Button onClick={save} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="admin-branding-save"><FloppyDisk size={14} className="mr-1" /> Save branding</Button></div>
      </div>
      <div className="border border-zinc-200 bg-gradient-to-br from-adb-navy/5 to-adb-green/5 rounded-sm p-5">
        <div className="text-[10px] uppercase tracking-widest text-adb-navy font-bold">Preview</div>
        <div className="mt-3 border border-zinc-200 bg-white rounded-sm p-5">
          <div className="flex items-center gap-2">
            {b.logo_path ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${b.logo_path}`} alt="logo" className="h-8 w-auto max-w-[100px] object-contain" />
            ) : (
              <div className="w-8 h-8 rounded-sm grid place-items-center" style={{ background: b.primary_color }}><span className="text-white font-bold text-xs">CO</span></div>
            )}
            <div>
              <div className="font-display font-bold text-sm">Your Company</div>
              {b.tagline && <div className="text-[10px] text-zinc-500 italic">{b.tagline}</div>}
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-100 text-[10px] text-zinc-500 font-mono-plex">
            <div className="w-2 h-2 rounded-full inline-block mr-1" style={{ background: b.accent_color }}></div>
            Powered by <span className="font-bold text-adb-navy">Zenith360 HR</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------ Policies ------
function PoliciesPanel({ params }) {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState(POLICY_KINDS[0].v);
  const [current, setCurrent] = useState({ title: "", body: "", version: "1.0" });
  const [aiBusy, setAiBusy] = useState(false);
  const [mode, setMode] = useState("preview");

  const load = () => api.get("/admin/policies", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);

  useEffect(() => {
    const existing = rows.find((r) => r.kind === kind);
    setCurrent(existing ? { title: existing.title, body: existing.body, version: existing.version || "1.0" } : { title: POLICY_KINDS.find((k) => k.v === kind).label, body: "", version: "1.0" });
  }, [kind, rows]);

  const save = async () => {
    try { await api.post("/admin/policies", { ...current, kind, ...params }); toast.success("Policy saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const askAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/ai-generate", { kind: "policy", subject: POLICY_KINDS.find((k) => k.v === kind).label, context: "For an India-based professional services / HR outsourcing company. Include applicable Indian statutory references." });
      setCurrent({ ...current, body: data.content });
      toast.success("Draft generated · edit and save");
    } catch (e) { toast.error(formatApiError(e)); }
    setAiBusy(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="border border-zinc-200 bg-white rounded-sm p-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2 px-2">Policies</div>
        <div className="space-y-0.5" data-testid="admin-policies-list">
          {POLICY_KINDS.map((k) => {
            const has = rows.find((r) => r.kind === k.v);
            return (
              <button key={k.v} onClick={() => setKind(k.v)} data-testid={`admin-policy-${k.v}`}
                className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-sm text-left text-sm transition-colors ${kind === k.v ? "bg-adb-navy text-white" : "hover:bg-zinc-50 text-zinc-700"}`}>
                <span className="truncate">{k.label}</span>
                {has && <CheckCircle size={12} weight="fill" className={kind === k.v ? "text-adb-green" : "text-emerald-600"} />}
              </button>
            );
          })}
        </div>
      </div>
      <div className="lg:col-span-3 space-y-3">
        <div className="border border-zinc-200 bg-white rounded-sm p-4 flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label className="text-xs">Title</Label><Input value={current.title} onChange={(e) => setCurrent({ ...current, title: e.target.value })} className="rounded-sm mt-1 w-[300px]" data-testid="admin-policy-title" /></div>
            <div><Label className="text-xs">Version</Label><Input value={current.version} onChange={(e) => setCurrent({ ...current, version: e.target.value })} className="rounded-sm mt-1 h-9 w-24 font-mono-plex" /></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-zinc-200 rounded-sm overflow-hidden">
              <button onClick={() => setMode("preview")} className={`px-3 py-1.5 text-xs font-semibold ${mode === "preview" ? "bg-adb-navy text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid="admin-policy-mode-preview"><Eye size={12} className="inline mr-1" /> Preview</button>
              <button onClick={() => setMode("source")} className={`px-3 py-1.5 text-xs font-semibold ${mode === "source" ? "bg-adb-navy text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid="admin-policy-mode-source"><PencilSimple size={12} className="inline mr-1" /> Edit source</button>
            </div>
            <Button onClick={askAI} disabled={aiBusy} className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold" data-testid="admin-policy-ai">
              <Sparkle size={14} weight="fill" className="mr-1" /> {aiBusy ? "Generating…" : "Use Zenith360AI"}
            </Button>
            <Button onClick={save} disabled={!current.body} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800" data-testid="admin-policy-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
          </div>
        </div>

        {mode === "source" ? (
          <div className="border border-zinc-200 bg-zinc-950 rounded-sm p-0 overflow-hidden">
            <div className="px-3 py-1.5 bg-zinc-800 text-zinc-300 text-[10px] uppercase tracking-widest font-mono-plex">HTML source</div>
            <Textarea rows={20} value={current.body} onChange={(e) => setCurrent({ ...current, body: e.target.value })} placeholder="<h3>Purpose</h3>…" className="rounded-none bg-zinc-950 text-emerald-200 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-plex text-xs" data-testid="admin-policy-body" />
          </div>
        ) : (
          <div className="border border-zinc-200 bg-gradient-to-b from-white to-zinc-50 rounded-sm shadow-sm overflow-hidden" data-testid="admin-policy-preview">
            <div className="bg-adb-navy px-6 py-4">
              <div className="text-white/60 text-[10px] uppercase tracking-[0.3em] font-bold">Policy · v{current.version}</div>
              <div className="text-white font-display text-2xl font-bold mt-1">{current.title || "Untitled policy"}</div>
            </div>
            <div className="p-8 min-h-[400px]">
              {current.body ? (
                <article
                  className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-adb-navy prose-headings:font-bold prose-h3:text-lg prose-h3:mt-4 prose-p:text-zinc-700 prose-p:leading-relaxed prose-ul:my-2 prose-li:my-0.5 prose-strong:text-adb-navy"
                  dangerouslySetInnerHTML={{ __html: current.body }}
                />
              ) : (
                <div className="text-center py-16">
                  <Sparkle size={32} weight="fill" className="mx-auto text-adb-green" />
                  <div className="mt-3 font-display text-lg font-bold">Empty policy</div>
                  <div className="text-xs text-zinc-500 mt-1 max-w-md mx-auto">Click <span className="font-semibold text-adb-navy">"Use Zenith360AI"</span> to auto-draft a professional India-first {POLICY_KINDS.find((k) => k.v === kind)?.label.toLowerCase()}, or switch to <span className="font-semibold">Edit source</span> to paste your own.</div>
                </div>
              )}
            </div>
            <div className="border-t border-zinc-100 px-6 py-3 flex items-center justify-between bg-zinc-50">
              <div className="text-[10px] text-zinc-500 font-mono-plex">Powered by <span className="text-adb-navy font-bold">Zenith360 HR</span></div>
              <div className="w-2 h-2 rounded-full bg-adb-green"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------ Letter templates ------
const PLACEHOLDERS = ["employee_name", "designation", "department", "tenant_name", "joining_date", "ctc_annual", "location", "employee_code", "today", "manager_name", "grade"];
const SAMPLE = {
  employee_name: "Priya Menon", designation: "Senior Software Engineer", department: "Engineering",
  tenant_name: "Acme Technologies Pvt Ltd", joining_date: "01 Apr 2026", ctc_annual: "₹ 24,00,000",
  location: "Bengaluru", employee_code: "ACM-042", today: new Date().toLocaleDateString(),
  manager_name: "Rahul Sharma", grade: "L4",
};

function renderPreview(html) {
  if (!html) return "";
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => SAMPLE[k] || `[${k}]`);
}

function LettersPanel({ params }) {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState(LETTER_KINDS[0].v);
  const [current, setCurrent] = useState({ title: "", body: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const [mode, setMode] = useState("preview");

  const load = () => api.get("/admin/letter-templates", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);

  useEffect(() => {
    const existing = rows.find((r) => r.kind === kind);
    setCurrent(existing ? { title: existing.title, body: existing.body } : { title: LETTER_KINDS.find((k) => k.v === kind).label, body: "" });
  }, [kind, rows]);

  const save = async () => {
    try { await api.post("/admin/letter-templates", { ...current, kind, ...params }); toast.success("Template saved · will be used in HR letters"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const askAI = async () => {
    setAiBusy(true);
    const start = Date.now();
    try {
      const label = LETTER_KINDS.find((k) => k.v === kind).label;
      const { data } = await api.post("/admin/ai-generate", { kind: "letter", subject: label, context: "For an India-based professional services / HR outsourcing employee. Use Jinja placeholders like {{ employee_name }}." }, { timeout: 60000 });
      setCurrent({ ...current, body: data.content });
      setMode("preview");
      toast.success(`Draft ready in ${Math.round((Date.now() - start) / 1000)}s`);
    } catch (e) {
      if (e?.response?.status === 502) {
        try {
          const label = LETTER_KINDS.find((k) => k.v === kind).label;
          const { data } = await api.post("/admin/ai-generate", { kind: "letter", subject: label }, { timeout: 60000 });
          setCurrent({ ...current, body: data.content }); setMode("preview");
          toast.success("Draft ready (retry)"); setAiBusy(false); return;
        } catch (e2) { toast.error(formatApiError(e2)); }
      } else { toast.error(formatApiError(e)); }
    }
    setAiBusy(false);
  };

  const insertPlaceholder = (k) => {
    setMode("source");
    setCurrent({ ...current, body: (current.body || "") + ` {{ ${k} }} ` });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="border border-zinc-200 bg-white rounded-sm p-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2 px-2">Templates</div>
        <div className="space-y-0.5">
          {LETTER_KINDS.map((k) => {
            const has = rows.find((r) => r.kind === k.v);
            return (
              <button key={k.v} onClick={() => setKind(k.v)} data-testid={`admin-letter-${k.v}`}
                className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-sm text-left text-sm transition-colors ${kind === k.v ? "bg-adb-navy text-white" : "hover:bg-zinc-50 text-zinc-700"}`}>
                <span className="truncate">{k.label}</span>
                {has && <CheckCircle size={12} weight="fill" className={kind === k.v ? "text-adb-green" : "text-emerald-600"} />}
              </button>
            );
          })}
        </div>
        <div className="mt-4 pt-3 border-t border-zinc-100 px-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Placeholders</div>
          <div className="flex flex-wrap gap-1">
            {PLACEHOLDERS.map((p) => (
              <button key={p} onClick={() => insertPlaceholder(p)} className="text-[10px] px-1.5 py-0.5 border border-zinc-200 rounded-sm hover:border-adb-navy hover:bg-adb-navy hover:text-white transition-colors font-mono-plex">{p}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:col-span-3 space-y-3">
        <div className="border border-zinc-200 bg-white rounded-sm p-4 flex flex-wrap items-end justify-between gap-3">
          <div><Label className="text-xs">Letter title</Label><Input value={current.title} onChange={(e) => setCurrent({ ...current, title: e.target.value })} className="rounded-sm mt-1 w-[320px]" data-testid="admin-letter-title" /></div>
          <div className="flex items-center gap-2">
            <div className="flex border border-zinc-200 rounded-sm overflow-hidden">
              <button onClick={() => setMode("preview")} className={`px-3 py-1.5 text-xs font-semibold ${mode === "preview" ? "bg-adb-navy text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid="admin-letter-mode-preview"><Eye size={12} className="inline mr-1" /> Preview</button>
              <button onClick={() => setMode("source")} className={`px-3 py-1.5 text-xs font-semibold ${mode === "source" ? "bg-adb-navy text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid="admin-letter-mode-source"><PencilSimple size={12} className="inline mr-1" /> Edit source</button>
            </div>
            <Button onClick={askAI} disabled={aiBusy} className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold" data-testid="admin-letter-ai">
              <Sparkle size={14} weight="fill" className="mr-1" /> {aiBusy ? "Generating…" : "Use Zenith360AI"}
            </Button>
            <Button onClick={save} disabled={!current.body} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800" data-testid="admin-letter-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
          </div>
        </div>

        {mode === "source" ? (
          <div className="border border-zinc-200 bg-zinc-950 rounded-sm p-0 overflow-hidden">
            <div className="px-3 py-1.5 bg-zinc-800 text-zinc-300 text-[10px] uppercase tracking-widest font-mono-plex flex items-center justify-between">
              <span>HTML source · use Jinja placeholders</span>
              <span className="text-adb-green">{"{{ employee_name }}"}</span>
            </div>
            <Textarea rows={22} value={current.body} onChange={(e) => setCurrent({ ...current, body: e.target.value })} placeholder="<p>Dear {{ employee_name }},</p>…" className="rounded-none bg-zinc-950 text-emerald-200 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-plex text-xs" data-testid="admin-letter-body" />
          </div>
        ) : (
          <div className="rounded-sm shadow-md overflow-hidden bg-white" data-testid="admin-letter-preview">
            {/* Letterhead with sample logo band */}
            <div className="bg-adb-navy px-8 py-5 flex items-center justify-between">
              <div>
                <div className="text-white font-display font-bold text-lg">Acme Technologies Pvt Ltd</div>
                <div className="text-white/60 text-[10px] uppercase tracking-widest mt-0.5">Sample preview · your logo &amp; header appear in generated PDFs</div>
              </div>
              <div className="text-adb-green text-[10px] uppercase tracking-widest font-bold">Human Resources</div>
            </div>
            <div className="h-1 bg-adb-green"></div>
            <div className="p-10 min-h-[500px] bg-[#fbfaf6]" style={{ fontFamily: "'Instrument Serif', 'Times New Roman', serif" }}>
              <div className="text-adb-navy font-display text-2xl font-bold mb-1">{current.title || "Untitled letter"}</div>
              <div className="text-xs text-zinc-500 mb-6 font-sans">{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</div>
              {current.body ? (
                <article
                  className="max-w-none text-zinc-800 leading-relaxed"
                  style={{ fontSize: "15px", fontFamily: "Manrope, sans-serif" }}
                  dangerouslySetInnerHTML={{ __html: renderPreview(current.body) }}
                />
              ) : (
                <div className="text-center py-16">
                  <FileText size={32} weight="duotone" className="mx-auto text-adb-navy" />
                  <div className="mt-3 font-display text-lg font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Empty template</div>
                  <div className="text-xs text-zinc-500 mt-1 max-w-md mx-auto" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Click <span className="font-semibold text-adb-navy">"Use Zenith360AI"</span> to auto-draft a professional {LETTER_KINDS.find((k) => k.v === kind)?.label.toLowerCase()}, or switch to <span className="font-semibold">Edit source</span> and start with a blank slate. Use the placeholder chips in the sidebar to insert dynamic fields.
                  </div>
                </div>
              )}
              <div className="mt-8 pt-4 border-t border-zinc-200 text-xs text-zinc-600 font-sans" style={{ fontFamily: "Manrope, sans-serif" }}>
                Sincerely,<br/><b>HR Team</b><br/>Acme Technologies Pvt Ltd
              </div>
            </div>
            <div className="border-t border-zinc-100 px-8 py-3 flex items-center justify-between bg-white">
              <div className="text-[10px] text-zinc-500 font-mono-plex">Sample data — actual values injected at generation time</div>
              <div className="text-[10px] font-mono-plex text-zinc-500">Powered by <span className="text-adb-navy font-bold">Zenith360 HR</span> <span className="inline-block w-1.5 h-1.5 rounded-full bg-adb-green ml-1 -mb-0.5"></span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------ Timesheet config ------
function TimesheetPanel({ params }) {
  const [cfg, setCfg] = useState({ require_daily_submission: true, auto_lock_after_days: 7, minimum_daily_hours: 8, maximum_daily_hours: 12, weekend_off_days: [5, 6], link_with_attendance: true, require_project_allocation: true });
  useEffect(() => { api.get("/admin/timesheet-config", { params }).then(({ data }) => setCfg(data)); /* eslint-disable-next-line */ }, [params.tenant_id]);
  const save = async () => { try { await api.post("/admin/timesheet-config", { ...cfg, ...params }); toast.success("Config saved"); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-6 max-w-3xl space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex items-center gap-2 border border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-adb-navy transition-colors">
          <input type="checkbox" checked={cfg.link_with_attendance} onChange={(e) => setCfg({ ...cfg, link_with_attendance: e.target.checked })} data-testid="ts-link-att" />
          <div><div className="text-sm font-semibold">Link with attendance</div><div className="text-[10px] text-zinc-500">Auto-populate timesheet from check-in/out data.</div></div>
        </label>
        <label className="flex items-center gap-2 border border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-adb-navy transition-colors">
          <input type="checkbox" checked={cfg.require_daily_submission} onChange={(e) => setCfg({ ...cfg, require_daily_submission: e.target.checked })} data-testid="ts-daily" />
          <div><div className="text-sm font-semibold">Require daily submission</div><div className="text-[10px] text-zinc-500">Otherwise weekly is fine.</div></div>
        </label>
        <label className="flex items-center gap-2 border border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-adb-navy transition-colors">
          <input type="checkbox" checked={cfg.require_project_allocation} onChange={(e) => setCfg({ ...cfg, require_project_allocation: e.target.checked })} data-testid="ts-alloc" />
          <div><div className="text-sm font-semibold">Require project allocation</div><div className="text-[10px] text-zinc-500">Employees must have an active allocation to log hours.</div></div>
        </label>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-xs">Min daily hours</Label><Input type="number" step="0.5" value={cfg.minimum_daily_hours} onChange={(e) => setCfg({ ...cfg, minimum_daily_hours: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" data-testid="ts-min" /></div>
        <div><Label className="text-xs">Max daily hours</Label><Input type="number" step="0.5" value={cfg.maximum_daily_hours} onChange={(e) => setCfg({ ...cfg, maximum_daily_hours: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" data-testid="ts-max" /></div>
        <div><Label className="text-xs">Auto-lock after (days)</Label><Input type="number" value={cfg.auto_lock_after_days} onChange={(e) => setCfg({ ...cfg, auto_lock_after_days: parseInt(e.target.value) })} className="rounded-sm font-mono-plex" data-testid="ts-lock" /></div>
      </div>
      <div className="flex justify-end"><Button onClick={save} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="ts-save"><FloppyDisk size={14} className="mr-1" /> Save config</Button></div>
    </div>
  );
}

// ------ Salary defaults ------
function SalaryPanel({ params }) {
  const [s, setS] = useState({ default_currency: "INR", basic_percent: 40, hra_percent: 40, special_percent: 20, pf_applicable: true, esic_applicable: true, pt_applicable: true, pf_employee_rate: 12, pf_employer_rate: 12, esic_employee_rate: 0.75, esic_employer_rate: 3.25, esic_wage_ceiling: 21000, tds_new_regime_default: true, gratuity_days_per_year: 15 });
  useEffect(() => { api.get("/admin/salary-defaults", { params }).then(({ data }) => setS({ pf_applicable: true, esic_applicable: true, pt_applicable: true, ...data })); /* eslint-disable-next-line */ }, [params.tenant_id]);
  const save = async () => { try { await api.post("/admin/salary-defaults", { ...s, ...params }); toast.success("Salary defaults saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const total = Number(s.basic_percent || 0) + Number(s.hra_percent || 0) + Number(s.special_percent || 0);

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-6 max-w-4xl space-y-5">
      <div>
        <div className="font-display font-bold text-sm">Statutory applicability</div>
        <div className="text-[10px] text-zinc-500 mb-2">Turn statutory heads off if your establishment is not covered. Payroll runs will skip disabled heads for all employees.</div>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex items-center gap-2 border border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-adb-navy transition-colors">
            <input type="checkbox" checked={!!s.pf_applicable} onChange={(e) => setS({ ...s, pf_applicable: e.target.checked })} data-testid="sal-pf-applicable" />
            <div><div className="text-sm font-semibold">PF applicable</div><div className="text-[10px] text-zinc-500">EPFO code required if enabled.</div></div>
          </label>
          <label className="flex items-center gap-2 border border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-adb-navy transition-colors">
            <input type="checkbox" checked={!!s.esic_applicable} onChange={(e) => setS({ ...s, esic_applicable: e.target.checked })} data-testid="sal-esic-applicable" />
            <div><div className="text-sm font-semibold">ESIC applicable</div><div className="text-[10px] text-zinc-500">Only if 10+ employees and gross ≤ ceiling.</div></div>
          </label>
          <label className="flex items-center gap-2 border border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-adb-navy transition-colors">
            <input type="checkbox" checked={!!s.pt_applicable} onChange={(e) => setS({ ...s, pt_applicable: e.target.checked })} data-testid="sal-pt-applicable" />
            <div><div className="text-sm font-semibold">Professional Tax</div><div className="text-[10px] text-zinc-500">Varies by state. Off for states without PT.</div></div>
          </label>
        </div>
      </div>
      <div>
        <div className="font-display font-bold text-sm">Salary structure split</div>
        <div className="mt-2 grid grid-cols-4 gap-3">
          <div><Label className="text-xs">Currency</Label>
            <Select value={s.default_currency} onValueChange={(v) => setS({ ...s, default_currency: v })}>
              <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{["INR", "USD", "GBP", "EUR", "AED"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Basic %</Label><Input type="number" value={s.basic_percent} onChange={(e) => setS({ ...s, basic_percent: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" data-testid="sal-basic" /></div>
          <div><Label className="text-xs">HRA % (of basic)</Label><Input type="number" value={s.hra_percent} onChange={(e) => setS({ ...s, hra_percent: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
          <div><Label className="text-xs">Special allowance %</Label><Input type="number" value={s.special_percent} onChange={(e) => setS({ ...s, special_percent: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
        </div>
        <Badge variant="outline" className={`mt-2 rounded-sm ${total === 100 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>Total: {total}%</Badge>
      </div>
      <div className={s.pf_applicable || s.esic_applicable ? "" : "opacity-50 pointer-events-none"}>
        <div className="font-display font-bold text-sm">Statutory rates</div>
        <div className="mt-2 grid grid-cols-4 gap-3">
          <div><Label className="text-xs">PF employee %</Label><Input disabled={!s.pf_applicable} type="number" step="0.1" value={s.pf_employee_rate} onChange={(e) => setS({ ...s, pf_employee_rate: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
          <div><Label className="text-xs">PF employer %</Label><Input disabled={!s.pf_applicable} type="number" step="0.1" value={s.pf_employer_rate} onChange={(e) => setS({ ...s, pf_employer_rate: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
          <div><Label className="text-xs">ESIC employee %</Label><Input disabled={!s.esic_applicable} type="number" step="0.01" value={s.esic_employee_rate} onChange={(e) => setS({ ...s, esic_employee_rate: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
          <div><Label className="text-xs">ESIC employer %</Label><Input disabled={!s.esic_applicable} type="number" step="0.01" value={s.esic_employer_rate} onChange={(e) => setS({ ...s, esic_employer_rate: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
          <div><Label className="text-xs">ESIC wage ceiling (₹)</Label><Input disabled={!s.esic_applicable} type="number" value={s.esic_wage_ceiling} onChange={(e) => setS({ ...s, esic_wage_ceiling: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
          <div><Label className="text-xs">Gratuity days/yr</Label><Input type="number" step="0.5" value={s.gratuity_days_per_year} onChange={(e) => setS({ ...s, gratuity_days_per_year: parseFloat(e.target.value) })} className="rounded-sm font-mono-plex" /></div>
        </div>
      </div>
      <label className="flex items-center gap-2 border border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-adb-navy transition-colors max-w-md">
        <input type="checkbox" checked={s.tds_new_regime_default} onChange={(e) => setS({ ...s, tds_new_regime_default: e.target.checked })} data-testid="sal-regime" />
        <div><div className="text-sm font-semibold">Default to New Tax Regime</div><div className="text-[10px] text-zinc-500">Employees can override in their tax declaration.</div></div>
      </label>
      <div className="flex justify-end"><Button onClick={save} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="sal-save"><FloppyDisk size={14} className="mr-1" /> Save defaults</Button></div>
    </div>
  );
}


// ------ Bulk import ------
const BULK_ENTITIES = [
  { v: "clients", label: "Clients", hint: "name, gstin, billing_email, billing_currency, notes" },
  { v: "projects", label: "Projects", hint: "code, name, client_name, start_date, end_date, billable_rate_currency, billable_rate_amount, billing_type" },
  { v: "employees", label: "Employees", hint: "employee_code, first_name, last_name, email (optional), phone, date_of_birth, designation, department, date_of_joining, gender, employment_type" },
  { v: "leave_balances", label: "Leave balances", hint: "employee_name, employee_code, leave_type (annual/sick/casual), opening_leaves, leave_credited, leave_used, leave_balance, year" },
];

function BulkImportPanel({ params }) {
  const [entity, setEntity] = useState("clients");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [salaryTemplate, setSalaryTemplate] = useState("__default");

  useEffect(() => {
    api.get("/salary-templates", { params })
      .then(({ data }) => setTemplates(data || []))
      .catch(() => setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.tenant_id]);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/bulk-import/template/${entity}`);
      const blob = new Blob([data.content], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doPreview = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/bulk-import/${entity}/preview`, fd, { params, headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const doCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const body = { ...params, rows: preview.rows };
      if (entity === "employees" && salaryTemplate && salaryTemplate !== "__default") {
        body.salary_template_id = salaryTemplate;
      }
      const { data } = await api.post(`/bulk-import/${entity}/commit`, body);
      setResult(data);
      const failCount = data.failures?.length || 0;
      const dup = data.duplicates || 0;
      if (failCount > 0) {
        toast.error(`${failCount} row(s) failed to import — see the report below`);
      } else {
        toast.success(`Inserted ${data.inserted}${dup ? ` · ${dup} duplicates skipped` : ""}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setCommitting(false);
  };

  const downloadFailedRows = () => {
    if (!result?.failures?.length || !preview?.rows?.length) return;
    const failedLines = new Set(result.failures.map((f) => f.line));
    const failedRows = preview.rows.filter((r) => failedLines.has(r.line));
    if (!failedRows.length) {
      toast.error("Could not match failed rows to the original CSV");
      return;
    }
    // Union of all column keys in the failed rows (preserves order from the first row)
    const seen = new Set();
    const columns = [];
    for (const r of failedRows) {
      for (const k of Object.keys(r.data || {})) {
        if (!seen.has(k)) { seen.add(k); columns.push(k); }
      }
    }
    const failureByLine = new Map(result.failures.map((f) => [f.line, f.reason]));
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    // Prepend a comment header row and _reason column so HR sees why each row failed
    const csv = ["# Failed rows — fix the values and re-upload this file"];
    csv.push([...columns, "_failure_reason"].join(","));
    for (const r of failedRows) {
      csv.push([
        ...columns.map((c) => escape((r.data || {})[c])),
        escape(failureByLine.get(r.line) || ""),
      ].join(","));
    }
    const blob = new Blob([csv.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entity}-failed-rows.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${failedRows.length} failed row${failedRows.length === 1 ? "" : "s"}`);
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; };
  const ent = BULK_ENTITIES.find((e) => e.v === entity);
  const hasErrors = (preview?.error_count || 0) > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="border border-zinc-200 bg-white rounded-sm p-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2 px-2">Entities</div>
        <div className="space-y-0.5">
          {BULK_ENTITIES.map((e) => (
            <button key={e.v} onClick={() => { setEntity(e.v); reset(); }} data-testid={`bulk-entity-${e.v}`}
              className={`w-full text-left px-2 py-2 rounded-sm text-sm transition-colors ${entity === e.v ? "bg-adb-navy text-white" : "hover:bg-zinc-50 text-zinc-700"}`}>
              <div className="font-semibold">{e.label}</div>
              <div className={`text-[9px] font-mono-plex truncate ${entity === e.v ? "text-white/60" : "text-zinc-400"}`}>{e.hint}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3 space-y-3">
        <div className="border border-zinc-200 bg-white rounded-sm p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-bold text-lg">Bulk import · {ent?.label}</div>
              <div className="text-xs text-zinc-500 mt-0.5">Download the template, fill in your rows, then upload the CSV. We'll validate and preview before writing.</div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="rounded-sm" data-testid="bulk-template-btn"><DownloadSimple size={14} className="mr-1" /> Template CSV</Button>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs" data-testid="bulk-file-input" />
            <Button onClick={doPreview} disabled={!file || busy} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800" data-testid="bulk-preview-btn"><Eye size={14} className="mr-1" /> {busy ? "Parsing…" : "Preview"}</Button>
            {preview && <Button variant="outline" onClick={reset} className="rounded-sm" data-testid="bulk-reset-btn"><ArrowsClockwise size={14} className="mr-1" /> Reset</Button>}
          </div>
          {entity === "employees" && (
            <div className="mt-3 flex items-center gap-2 flex-wrap pt-3 border-t border-zinc-100" data-testid="bulk-salary-template-row">
              <Label className="text-xs text-zinc-500 flex-none">Salary template:</Label>
              <Select value={salaryTemplate} onValueChange={setSalaryTemplate}>
                <SelectTrigger className="w-72 rounded-sm" data-testid="bulk-salary-template-select">
                  <SelectValue placeholder="Tenant default (40/20/40)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default">Tenant default (Basic 40 · HRA 20 · Special 40)</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[10px] text-zinc-500">Applied to any row with a positive <code className="bg-zinc-100 px-1">monthly_ctc</code>.</span>
            </div>
          )}
        </div>

        {preview && (
          <div className="border border-zinc-200 bg-white rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-sm border-adb-navy text-adb-navy">Rows: {preview.row_count}</Badge>
                <Badge variant="outline" className={`rounded-sm ${hasErrors ? "bg-red-50 border-red-300 text-red-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}>Errors: {preview.error_count}</Badge>
              </div>
              <Button onClick={doCommit} disabled={hasErrors || committing || result} className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold" data-testid="bulk-commit-btn">
                <FloppyDisk size={14} className="mr-1" /> {committing ? "Committing…" : result ? "Committed" : "Commit valid rows"}
              </Button>
            </div>
            {hasErrors && (
              <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-xs text-red-800 max-h-32 overflow-y-auto font-mono-plex">
                {(preview.errors || []).map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            {result && (
              <div className="border-b border-zinc-100" data-testid="bulk-result-block">
                <div className="px-4 py-2 bg-emerald-50 text-xs text-emerald-800 flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="rounded-sm bg-emerald-100 border-emerald-300 text-emerald-800">Inserted: {result.inserted}</Badge>
                  {(result.duplicates ?? 0) > 0 && (
                    <Badge variant="outline" className="rounded-sm bg-zinc-100 border-zinc-300 text-zinc-700">Duplicates: {result.duplicates}</Badge>
                  )}
                  {(result.skipped ?? 0) > 0 && (
                    <Badge variant="outline" className="rounded-sm bg-amber-50 border-amber-200 text-amber-800">Skipped: {result.skipped}</Badge>
                  )}
                  {(result.failures?.length ?? 0) > 0 && (
                    <Badge variant="outline" className="rounded-sm bg-rose-50 border-rose-200 text-rose-800">Failed: {result.failures.length}</Badge>
                  )}
                  <span className="ml-auto text-[10px] text-zinc-500">Refresh the corresponding page to see the new {ent?.label.toLowerCase()}.</span>
                </div>
                {(result.failures?.length ?? 0) > 0 && (
                  <div className="border-t border-rose-100" data-testid="bulk-failures-table">
                    <div className="px-3 py-1.5 bg-rose-50 border-b border-rose-100 flex items-center justify-between flex-wrap gap-2">
                      <div className="text-[10px] uppercase tracking-widest text-rose-800 font-medium">
                        {result.failures.length} row{result.failures.length === 1 ? "" : "s"} failed · fix &amp; re-upload
                      </div>
                      <Button size="sm" variant="outline" onClick={downloadFailedRows}
                        className="h-6 px-2 text-[10px] border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800 rounded-sm"
                        data-testid="bulk-download-failed-btn">
                        <DownloadSimple size={11} className="mr-1" /> Download failed rows only
                      </Button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-rose-50/60 text-rose-800 text-[10px] uppercase tracking-widest">
                          <tr>
                            <th className="text-left px-3 py-1.5 w-16">Line</th>
                            <th className="text-left px-3 py-1.5">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="font-mono-plex">
                          {result.failures.map((f, i) => (
                            <tr key={i} className="border-t border-rose-100 text-rose-700 hover:bg-rose-50/50" data-testid={`bulk-failure-L${f.line}`}>
                              <td className="px-3 py-1">L{f.line}</td>
                              <td className="px-3 py-1">{f.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {(result.duplicates ?? 0) > 0 && (result.failures?.length ?? 0) === 0 && (
                  <div className="px-4 py-2 bg-zinc-50 border-t border-zinc-100 text-[11px] text-zinc-600">
                    <b>{result.duplicates}</b> row(s) already existed and were skipped safely — no action needed.
                  </div>
                )}
              </div>
            )}
            <div className="max-h-[400px] overflow-y-auto">
              <table className="hr-table">
                <thead><tr><th>#</th>{Object.keys(preview.rows[0]?.data || {}).map((c) => <th key={c}>{c}</th>)}<th>Errors</th></tr></thead>
                <tbody>
                  {(preview.rows || []).slice(0, 200).map((r) => (
                    <tr key={r.line} className={r.errors?.length ? "bg-red-50/50" : ""}>
                      <td className="font-mono-plex text-xs text-zinc-500">L{r.line}</td>
                      {Object.entries(r.data || {}).map(([k, v]) => <td key={k} className="text-xs">{v || "—"}</td>)}
                      <td className="text-xs text-red-600">{(r.errors || []).join("; ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------ Onboarding kit ------
function OnboardingKitPanel({ params }) {
  const [kit, setKit] = useState({ welcome_message: "", welcome_video_path: "", welcome_pdf_path: "", required_doc_types: [] });
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const pdfRef = useRef(null);
  const [newDoc, setNewDoc] = useState("");

  useEffect(() => { api.get("/onboarding-kit", { params }).then(({ data }) => setKit(data)); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const uploadFile = async (file, target) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/uploads?purpose=onboarding_${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setKit((k) => ({ ...k, [target === "video" ? "welcome_video_path" : "welcome_pdf_path"]: data.path }));
      toast.success("File uploaded");
    } catch (e) { toast.error(formatApiError(e)); }

    setBusy(false);
  };

  const save = async () => {
    try { await api.post("/onboarding-kit", { ...kit, ...params }); toast.success("Onboarding kit saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addDoc = () => {
    if (!newDoc.trim()) return;
    setKit({ ...kit, required_doc_types: [...(kit.required_doc_types || []), newDoc.trim().toLowerCase().replace(/\s+/g, "_")] });
    setNewDoc("");
  };
  const removeDoc = (d) => setKit({ ...kit, required_doc_types: (kit.required_doc_types || []).filter((x) => x !== d) });

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-6 max-w-4xl space-y-5">
      <div>
        <div className="font-display font-bold text-sm">Welcome message</div>
        <Textarea rows={4} value={kit.welcome_message || ""} onChange={(e) => setKit({ ...kit, welcome_message: e.target.value })} placeholder="Welcome to the team! Here's what to expect in your first week…" className="rounded-sm mt-2" data-testid="onbkit-msg" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="border border-zinc-200 rounded-sm p-4 bg-zinc-50">
          <Label className="text-xs font-bold">Welcome video</Label>
          <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "video")} data-testid="onbkit-video-input" />
          <div className="mt-2 flex items-center gap-2">
            <Button variant="outline" onClick={() => videoRef.current?.click()} disabled={busy} className="rounded-sm text-xs"><UploadSimple size={12} className="mr-1" /> {busy ? "…" : "Upload video"}</Button>
            {kit.welcome_video_path && <span className="text-[10px] text-emerald-700 font-mono-plex truncate">{kit.welcome_video_path.split("/").pop()}</span>}
          </div>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4 bg-zinc-50">
          <Label className="text-xs font-bold">Welcome PDF (company handbook)</Label>
          <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "pdf")} data-testid="onbkit-pdf-input" />
          <div className="mt-2 flex items-center gap-2">
            <Button variant="outline" onClick={() => pdfRef.current?.click()} disabled={busy} className="rounded-sm text-xs"><UploadSimple size={12} className="mr-1" /> {busy ? "…" : "Upload PDF"}</Button>
            {kit.welcome_pdf_path && <span className="text-[10px] text-emerald-700 font-mono-plex truncate">{kit.welcome_pdf_path.split("/").pop()}</span>}
          </div>
        </div>
      </div>
      <div>
        <div className="font-display font-bold text-sm">Required documents for new joiners</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(kit.required_doc_types || []).map((d) => (
            <Badge key={d} variant="outline" className="rounded-sm bg-adb-navy/5 border-adb-navy/20 text-adb-navy font-mono-plex text-[10px]">
              {d.replace(/_/g, " ")}
              <button onClick={() => removeDoc(d)} className="ml-1.5 text-red-600 hover:opacity-70" data-testid={`onbkit-doc-del-${d}`}><Trash size={10} /></button>
            </Badge>
          ))}
        </div>
        <div className="mt-2 flex gap-2 max-w-md">
          <Input value={newDoc} onChange={(e) => setNewDoc(e.target.value)} placeholder="e.g. voter_id" className="rounded-sm font-mono-plex" data-testid="onbkit-doc-input" />
          <Button variant="outline" onClick={addDoc} className="rounded-sm" data-testid="onbkit-doc-add">Add</Button>
        </div>
      </div>
      <div className="flex justify-end"><Button onClick={save} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="onbkit-save"><FloppyDisk size={14} className="mr-1" /> Save kit</Button></div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Salary structure templates
// ---------------------------------------------------------------------------
function SalaryTemplatesPanel({ params }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultTemplate());
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/salary-templates", { params })
      .then(({ data }) => setTemplates(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [params.tenant_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPct = Math.round(
    ((form.basic_percent || 0) + (form.hra_percent || 0)
      + (form.special_allowance_percent || 0)
      + (form.conveyance_percent || 0)
      + (form.medical_allowance_percent || 0)) * 100
  ) / 100;
  const totalValid = Math.abs(totalPct - 100) < 0.01;

  const openNew = () => { setEditingId(null); setForm(defaultTemplate()); setOpen(true); };
  const openEdit = (t) => { setEditingId(t.id); setForm({ ...defaultTemplate(), ...t }); setOpen(true); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Template needs a name"); return; }
    if (!totalValid) { toast.error(`Splits must sum to 100% (currently ${totalPct}%)`); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/salary-templates/${editingId}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/salary-templates", { ...form, ...params });
        toast.success("Template created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Employees already using it keep their existing salary structure.`)) return;
    try {
      await api.delete(`/salary-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="salary-templates-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-display font-bold text-lg">Salary structure templates</div>
          <div className="text-xs text-zinc-500 mt-0.5">Define named earning splits + statutory defaults. Apply one when onboarding an employee (single or bulk) — mark one as <b>default</b> and it will be used automatically.</div>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800" data-testid="salary-template-new-btn">
          <Plus size={14} className="mr-1" /> New template
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 p-4">Loading templates…</div>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-zinc-200 bg-zinc-50 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="salary-templates-empty">
          No templates yet — new hires will use the built-in <b>40 / 20 / 40</b> default. Click <b>New template</b> to define one (e.g. <i>"Junior — 45/25/30"</i>, <i>"Senior — 40/20/40"</i>, <i>"Sales — 35/20/45 + high PT"</i>).
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => (
            <TemplateCard key={t.id} t={t} onEdit={() => openEdit(t)} onDelete={() => remove(t)} />
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="salary-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editingId ? "Edit template" : "New salary template"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder='e.g. "Junior — 45/25/30", "Senior — 40/20/40"'
                className="rounded-sm" data-testid="salary-template-name" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="When should HR pick this template? e.g. for L1-L3 junior hires with lower base."
                className="rounded-sm" data-testid="salary-template-desc" />
            </div>

            <div className="col-span-2 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mt-3 mb-1 border-b border-zinc-100 pb-1">
              Earnings split · must total 100%
            </div>
            <PercentField label="Basic %" v={form.basic_percent} on={(v) => setForm({ ...form, basic_percent: v })} tid="tpl-basic-pct" />
            <PercentField label="HRA %" v={form.hra_percent} on={(v) => setForm({ ...form, hra_percent: v })} tid="tpl-hra-pct" />
            <PercentField label="Special allowance %" v={form.special_allowance_percent} on={(v) => setForm({ ...form, special_allowance_percent: v })} tid="tpl-special-pct" />
            <PercentField label="Conveyance %" v={form.conveyance_percent} on={(v) => setForm({ ...form, conveyance_percent: v })} tid="tpl-conveyance-pct" />
            <PercentField label="Medical allowance %" v={form.medical_allowance_percent} on={(v) => setForm({ ...form, medical_allowance_percent: v })} tid="tpl-medical-pct" />
            <div className="text-xs flex justify-between items-center border-t border-zinc-100 pt-2 col-span-1">
              <span className="text-zinc-500 uppercase tracking-widest text-[10px]">Total</span>
              <span className={`font-bold text-lg font-mono-plex ${totalValid ? "text-emerald-700" : "text-rose-700"}`}
                data-testid="tpl-total-pct">{totalPct}%</span>
            </div>

            <div className="col-span-2 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mt-3 mb-1 border-b border-zinc-100 pb-1">
              Statutory deductions
            </div>
            <PercentField label="Employee PF % of Basic" v={form.employee_pf_percent_of_basic} on={(v) => setForm({ ...form, employee_pf_percent_of_basic: v })} tid="tpl-ee-pf-pct" />
            <PercentField label="Employer PF % of Basic" v={form.employer_pf_percent_of_basic} on={(v) => setForm({ ...form, employer_pf_percent_of_basic: v })} tid="tpl-er-pf-pct" />
            <NumberField label="PF cap (₹/month)" v={form.pf_cap} on={(v) => setForm({ ...form, pf_cap: v })} tid="tpl-pf-cap" />
            <div className="space-y-1">
              <Label className="text-xs">Professional Tax mode</Label>
              <Select value={form.pt_mode || "fixed"} onValueChange={(v) => setForm({ ...form, pt_mode: v })}>
                <SelectTrigger className="rounded-sm" data-testid="tpl-pt-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed amount</SelectItem>
                  <SelectItem value="state_slab">State-wise & gender-wise slabs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.pt_mode || "fixed") === "fixed" ? (<>
              <NumberField label="Professional Tax (₹/month)" v={form.professional_tax} on={(v) => setForm({ ...form, professional_tax: v })} tid="tpl-pt" />
              <NumberField label="Skip PT below CTC (₹)" v={form.professional_tax_min_ctc} on={(v) => setForm({ ...form, professional_tax_min_ctc: v })} tid="tpl-pt-min" />
            </>) : (
              <div className="space-y-1">
                <Label className="text-xs">Default PT state (when employee has none)</Label>
                <PtStateSelect value={form.pt_state || ""} onChange={(v) => setForm({ ...form, pt_state: v })} />
                <div className="text-[11px] text-zinc-500">Uses each employee's PT state + gender (e.g. Maharashtra: men above ₹7,500, women above ₹25,000; ₹300 in February).</div>
              </div>
            )}
            <PercentField label="Default TDS %" v={form.tds_percent} on={(v) => setForm({ ...form, tds_percent: v })} tid="tpl-tds" />

            <label className="col-span-2 flex items-center gap-2 mt-3 cursor-pointer" data-testid="tpl-is-default-label">
              <input type="checkbox" checked={!!form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                className="w-4 h-4 rounded" data-testid="tpl-is-default" />
              <span className="text-sm">Make this the tenant's <b>default</b> template — auto-applied when no template is picked at onboarding.</span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving || !totalValid} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="tpl-save-btn">
              <FloppyDisk size={14} className="mr-1" />
              {saving ? "Saving…" : (editingId ? "Update template" : "Create template")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateCard({ t, onEdit, onDelete }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4 space-y-2" data-testid={`salary-template-card-${t.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display font-bold flex items-center gap-2">
            {t.name}
            {t.is_default && <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Default</Badge>}
          </div>
          {t.description && <div className="text-xs text-zinc-500 mt-0.5">{t.description}</div>}
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 px-2" data-testid={`salary-template-edit-${t.id}`}><PencilSimple size={12} /></Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 px-2 text-rose-700 hover:bg-rose-50" data-testid={`salary-template-delete-${t.id}`}><Trash size={12} /></Button>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2 text-[11px] pt-2 border-t border-zinc-100 font-mono-plex">
        <SplitPct label="Basic" v={t.basic_percent} />
        <SplitPct label="HRA" v={t.hra_percent} />
        <SplitPct label="Special" v={t.special_allowance_percent} />
        <SplitPct label="Conv" v={t.conveyance_percent} />
        <SplitPct label="Med" v={t.medical_allowance_percent} />
      </div>
      <div className="text-[10px] text-zinc-500 pt-1 border-t border-zinc-100">
        PF {t.employee_pf_percent_of_basic}% of Basic · cap ₹{t.pf_cap} · PT ₹{t.professional_tax}/mo · TDS {t.tds_percent}%
      </div>
    </div>
  );
}

function SplitPct({ label, v }) {
  const value = Number(v || 0);
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-400">{label}</div>
      <div className="font-bold">{value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}%</div>
    </div>
  );
}

function PercentField({ label, v, on, tid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" step="0.5" min="0" max="100" value={v ?? 0}
        onChange={(e) => on(parseFloat(e.target.value) || 0)}
        className="rounded-sm" data-testid={tid} />
    </div>
  );
}

function NumberField({ label, v, on, tid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" min="0" value={v ?? 0}
        onChange={(e) => on(parseFloat(e.target.value) || 0)}
        className="rounded-sm" data-testid={tid} />
    </div>
  );
}

function defaultTemplate() {
  return {
    name: "", description: "",
    basic_percent: 40, hra_percent: 20, special_allowance_percent: 40,
    conveyance_percent: 0, medical_allowance_percent: 0,
    employee_pf_percent_of_basic: 12, employer_pf_percent_of_basic: 12, pf_cap: 1800,
    professional_tax: 200, professional_tax_min_ctc: 25000, pt_mode: "fixed", pt_state: "",
    tds_percent: 0, is_default: false,
  };
}

function PtStateSelect({ value, onChange }) {
  const [states, setStates] = useState([]);
  useEffect(() => { api.get("/salary-templates/pt-states").then(({ data }) => setStates(data)).catch(() => {}); }, []);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm" data-testid="tpl-pt-state"><SelectValue placeholder="Select state" /></SelectTrigger>
      <SelectContent>{states.map((s) => <SelectItem key={s.state} value={s.state}>{s.state}{s.has_pt ? (s.gender_specific ? " (gender-wise)" : "") : " (no PT)"}</SelectItem>)}</SelectContent>
    </Select>
  );
}

